'use client';

import { Icon } from '@/general/Icons/Icon';
import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';

import { Shade } from '@/general/components/Shade/Shade';

type SearchAlertToggleProps = {
  isActive: boolean;
  initialised?: boolean;
  disabled?: boolean;
  onHandleSwitch: (isActive: boolean) => Promise<void>;
};

export const SearchAlertToggle: FC<SearchAlertToggleProps> = ({
  isActive,
  disabled,
  initialised = true,
  onHandleSwitch,
}) => {
  const [mounted, setMounted] = useState<boolean>(false);
  const label = isActive ? 'Alert active' : 'Search alert';

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return false;
  }

  return (
    <label
      className={clsx(
        'c-shade_container relative m-0 flex h-10 items-center justify-start gap-2 overflow-hidden rounded-full py-2 pr-4 pl-2 transition-all duration-200 focus-within:ring-2 focus-within:ring-bor-pri-mut',
        {
          'c-shade_container--purple text-tex-inv-tie1': !isActive,
          'c-shade_container--green text-tex-def-tie1': isActive,
          'opacity-0': !initialised,
        },
      )}
      data-testid="search-alert-control"
      data-state={isActive ? 'active' : 'inactive'}
    >
      <Shade />
      <input
        type="checkbox"
        name="searchAlert"
        checked={isActive}
        onChange={() => onHandleSwitch(!isActive)}
        disabled={disabled}
        className="absolute -left-10 opacity-0"
        data-testid="SearchAlertToggle-input"
      />
      <div
        className={clsx('pointer-events-none relative h-6 w-11 rounded-full transition-all duration-200', {
          'bg-sta-on-inv-hov': !isActive,
          'bg-for-suc-bas': isActive,
        })}
      >
        <div
          className={clsx(
            'absolute top-0.5 left-0.5 size-5 rounded-full bg-for-neu-tie1 shadow-elevation-0dp transition-all duration-200',
            {
              'translate-x-5': isActive,
            },
          )}
        >
          <div
            className={clsx('absolute top-0 left-0 size-full text-for-pri-bas transition-opacity duration-200', {
              'opacity-0': isActive,
            })}
          >
            <Icon name="bell-off" size={20} />
          </div>
          <div
            className={clsx('absolute top-0 left-0 size-full text-ico-suc-tie1 transition-opacity duration-200', {
              'opacity-0': !isActive,
            })}
          >
            <Icon name="bell-on" size={20} />
          </div>
        </div>
      </div>
      <span className="c-fs-caption pointer-events-none relative font-extrabold whitespace-nowrap">{label}</span>
    </label>
  );
};
